import type { ReactNode } from 'react'

import { t } from '@ctcells/i18n'

import { cx } from '../atoms/cx.ts'
import { Icon } from '../atoms/Icon.tsx'

/**
 * 컨테이너가 화면 대신 그리는 **경계 상태 한 덩이**.
 *
 * 🔴 화면 컴포넌트가 아니라 **컨테이너**가 쓴다. 로딩·에러에서 화면을 아예 그리지 않는 이유는,
 *    목록 화면이 기본값 fixture 를 들고 있어서 «데이터 없음»으로 렌더하면 **목이 실물인 척**
 *    보이기 때문이다. 빈 상태는 화면의 `emptyLabel` 이 맡는다(0행 = 빈 목록이 정답).
 *
 * 🔴 **왼쪽 위 맨 글자였다**(2026-09-22 실측 — `.placeholder` 가 `color` 한 줄뿐이었다).
 *    1600×1000 화면의 구석에 회색 한 줄이 떠 있으면 **화면이 깨진 것처럼** 보인다.
 *    지금은 본문 한가운데에 글리프 + 문장으로 서고, `actions` 로 갈 곳을 같이 준다.
 * 🔴 색만으로 가르지 않는다 — 실패는 **글리프 · 굵기 · 색** 셋이 같이 말한다. `:root`
 *    (다른 앱 4개)는 위험색이 무채색이라 글리프와 굵기만 남는다. 그래도 구분된다.
 *
 * `role` 을 가른다 — 에러는 `alert`(즉시 읽힘), 로딩은 `status`(정중히 읽힘).
 */
export function ScreenState({
  text,
  tone,
  title,
  actions,
  detail,
  card,
}: {
  text: string
  tone?: 'error'
  /** 굵은 한 줄. 주면 `text` 는 그 아래 보조 문장이 된다 */
  title?: string | undefined
  /** 갈 곳. 막다른 화면을 만들지 않는다 */
  actions?: ReactNode
  /**
   * 🔴 **접힌 채로 선다.** 사람에게 먼저 보일 것이 아닌 원문(예외 메시지 따위)을
   *    여기 둔다 — 화면에는 한글 문장만 보이고, 필요한 사람만 펼쳐서 본다.
   */
  detail?: ReactNode
  /**
   * 면 위에 올린다. 🔴 **셸을 못 그리는 자리 전용**이다 — 헤더도 작업 줄도 없는 맨 바탕에
   *    글자만 뜨면 앱이 통째로 죽은 것처럼 보인다. 셸 안에서는 쓰지 않는다(본문이 이미 면이다).
   */
  card?: boolean
}) {
  const failed = tone === 'error'

  return (
    <section
      className={cx('placeholder', failed && 'placeholder--error')}
      role={failed ? 'alert' : 'status'}
    >
      <div className={cx('placeholder__box', card === true && 'placeholder__card')}>
        {failed ? <Icon name="alert" size="lg" /> : null}
        {/* 🔴 제목은 «제목»이다 — 보조기술이 화면의 머리로 읽는다(`role="heading"`) */}
        {title === undefined ? null : <h2 className="placeholder__title">{title}</h2>}
        <p className="placeholder__text">{text}</p>
        {actions === undefined ? null : <div className="placeholder__acts">{actions}</div>}
        {detail === undefined ? null : detail}
      </div>
    </section>
  )
}

/** 로딩 한 줄. 문구를 호출부마다 다르게 쓰지 않는다 */
export function ScreenLoading() {
  return <ScreenState text={t('state.loading')} />
}
